/**
 * ソロ・ドリル進捗の永続化（自前・GPL非依存・依存ゼロ）。
 * 設計: knowledge/architecture/solo-drill-daily-challenge.md §7（クライアント権威・ローカル進捗）
 *
 * 保存内容:
 *   - clearedIds: 取り切りクリア済みドリル ID 集合（昇格制の判定に使う）
 *   - noMissIds:  「1回ノーミス取り切り」を達成したドリル ID 集合（🥇相当のローカル自己ベスト）
 *
 * SCHEMA_VERSION 不整合は黙って破棄（既定の空進捗に戻す）＝state 層の契約（I-11）に倣う。
 * 本モジュールはローカル進捗のみ。本日のお題の 🥇 はサーバー権威（Phase 2）で別管理。
 */

import { readJson, writeJson } from './storage.js';

const KEY = 'pool.drill.progress.v1';
const SCHEMA_VERSION = 1;

export interface DrillProgress {
  /** クリア済みドリル ID（順序不問・重複なし）。 */
  readonly clearedIds: readonly string[];
  /** ノーミス取り切り達成のドリル ID。 */
  readonly noMissIds: readonly string[];
}

interface PersistedDrillProgress extends DrillProgress {
  readonly v: number;
}

const EMPTY: DrillProgress = { clearedIds: [], noMissIds: [] };

const sanitizeIds = (raw: readonly string[] | undefined): string[] => {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v === 'string' && v.length > 0 && !out.includes(v)) out.push(v);
  }
  return out;
};

/** 進捗を読み込む。未設定・破損・版不整合なら空進捗。 */
export const loadDrillProgress = (): DrillProgress => {
  const raw = readJson<PersistedDrillProgress>(KEY);
  if (!raw || typeof raw !== 'object' || raw.v !== SCHEMA_VERSION) return EMPTY;
  return {
    clearedIds: sanitizeIds(raw.clearedIds),
    noMissIds: sanitizeIds(raw.noMissIds),
  };
};

/** 進捗を保存する（失敗時はログのみ）。 */
export const saveDrillProgress = (progress: DrillProgress): void => {
  const payload: PersistedDrillProgress = {
    v: SCHEMA_VERSION,
    clearedIds: sanitizeIds(progress.clearedIds),
    noMissIds: sanitizeIds(progress.noMissIds),
  };
  writeJson(KEY, payload);
};

/**
 * クリアを記録して新しい進捗を返す（純粋・呼び出し側が保存する）。
 * noMiss=true のときのみ noMissIds にも追加する。
 */
export const recordDrillClear = (
  progress: DrillProgress,
  drillId: string,
  noMiss: boolean,
): DrillProgress => {
  const clearedIds = progress.clearedIds.includes(drillId)
    ? progress.clearedIds
    : [...progress.clearedIds, drillId];
  const noMissIds =
    noMiss && !progress.noMissIds.includes(drillId)
      ? [...progress.noMissIds, drillId]
      : progress.noMissIds;
  return { clearedIds, noMissIds };
};

/**
 * サーバー権威の進捗をローカルへ和集合マージする（authOk 後の復元用・純粋）。
 * ログイン時はサーバーが正本だが、未送信のローカル達成を失わないよう union を取る。
 */
export const mergeDrillProgress = (
  local: DrillProgress,
  server: {
    readonly clearedIds?: readonly string[];
    readonly noMissIds?: readonly string[];
  },
): DrillProgress => {
  const union = (
    a: readonly string[],
    b: readonly string[] | undefined,
  ): string[] => {
    const out = sanitizeIds(a);
    for (const v of sanitizeIds(b)) if (!out.includes(v)) out.push(v);
    return out;
  };
  return {
    clearedIds: union(local.clearedIds, server.clearedIds),
    noMissIds: union(local.noMissIds, server.noMissIds),
  };
};
